import { useCallback, useEffect, useRef } from 'react';

// Based on: https://stackoverflow.com/questions/56283920/how-to-debounce-a-callback-in-functional-component-using-hooks
const useDebouncedFunc = <A extends any[]>(func: (...args: A) => void, wait: number = 500) => {
  const argsRef = useRef<A>(undefined);
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const funcRef = useRef(func);
  useEffect(() => {
    funcRef.current = func;
  }, [func]);

  // make sure our timeout gets cleared if
  // our consuming component gets unmounted
  useEffect(
    () => () => {
      if (timeout.current) clearTimeout(timeout.current);
    },
    [],
  );

  const debounced = useCallback(
    (...args: A) => {
      // capture latest args
      argsRef.current = args;
      // clear debounce timer
      if (timeout.current) clearTimeout(timeout.current);
      // start waiting again
      timeout.current = setTimeout(() => {
        if (argsRef.current) funcRef.current(...argsRef.current);
      }, wait);
    },
    [wait],
  );

  // Run any pending debounced call immediately and wait for it to finish. Use before an
  // action that depends on the latest edit having been saved (e.g. submitting a form).
  const flush = useCallback(async () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
    if (!argsRef.current) return;
    const args = argsRef.current;
    argsRef.current = undefined;
    await funcRef.current(...args);
  }, []);

  return Object.assign(debounced, { flush });
};

export default useDebouncedFunc;
